const RPL_TITLE_PATTERN = /\(RPL\)/i;

export function isRplCourseTitle(title?: string | null): boolean {
  return typeof title === 'string' && RPL_TITLE_PATTERN.test(title);
}

export function isRplEvent(
  event?: {
    title?: string | null;
    detail?: { title?: string | null } | null;
  } | null,
): boolean {
  if (!event) return false;
  return isRplCourseTitle(event.detail?.title) || isRplCourseTitle(event.title);
}

type EnrollmentMetadataItem = {
  id?: string;
  title?: string | null;
  detail_id?: string;
  detail?: { id?: string; title?: string | null } | null;
};

type EnrollmentCourse = {
  event?:
    | (Parameters<typeof isRplEvent>[0] & {
        detail_id?: string | null;
      })
    | null;
};

type EnrollmentWithMetadata = {
  metadata?: EnrollmentMetadataItem[] | unknown;
  courses?: EnrollmentCourse[] | null;
};

const CLOSED_ENROLLMENT_STATUSES = [
  'draft',
  'cancelled',
  'canceled',
  'refunded',
];

function metadataDetailId(item: EnrollmentMetadataItem) {
  return item.detail_id ?? item.detail?.id ?? item.id ?? null;
}

export function metadataItemIsRpl(
  item: unknown,
  rplEventDetailIds?: ReadonlySet<string>,
): boolean {
  if (!item || typeof item !== 'object') return false;
  const metadataItem = item as EnrollmentMetadataItem;
  if (
    isRplCourseTitle(metadataItem.detail?.title) ||
    isRplCourseTitle(metadataItem.title)
  ) {
    return true;
  }

  const detailId = metadataDetailId(metadataItem);
  return Boolean(detailId && rplEventDetailIds?.has(detailId));
}

export function enrollmentRecordIsRpl(
  enrollment: EnrollmentWithMetadata | null | undefined,
  rplEventDetailIds?: ReadonlySet<string>,
): boolean {
  if (!enrollment) return false;

  if (
    Array.isArray(enrollment.metadata) &&
    enrollment.metadata.some((item) =>
      metadataItemIsRpl(item, rplEventDetailIds),
    )
  ) {
    return true;
  }

  return (
    enrollment.courses?.some((course) => {
      if (isRplEvent(course.event)) return true;
      const detailId = course.event?.detail_id;
      return Boolean(detailId && rplEventDetailIds?.has(detailId));
    }) ?? false
  );
}

export function enrollmentIncludesProgrammeRpl(
  enrollments: EnrollmentWithMetadata[] | undefined,
  rplEventDetailIds: ReadonlySet<string>,
): boolean {
  if (!enrollments?.length) return false;

  return enrollments.some((enrollment) =>
    enrollmentRecordIsRpl(enrollment, rplEventDetailIds),
  );
}

export function isRplLearnerRecord(input: {
  enrollments?: EnrollmentWithMetadata[] | null;
  registrations?: Array<{ event?: Parameters<typeof isRplEvent>[0] }> | null;
  rplEventDetailIds?: ReadonlySet<string>;
}): boolean {
  const detailIds = input.rplEventDetailIds ?? new Set<string>();
  if (
    enrollmentIncludesProgrammeRpl(input.enrollments || undefined, detailIds)
  ) {
    return true;
  }

  return (
    input.registrations?.some((registration) =>
      isRplEvent(registration.event),
    ) ?? false
  );
}

function metadataContains(detailId: string) {
  return [
    { metadata: { _contains: [{ detail_id: detailId }] } },
    { metadata: { _contains: [{ detail: { id: detailId } }] } },
    { metadata: { _contains: [{ id: detailId }] } },
  ];
}

/** Server-side match for learners on an RPL course, including those with no practical date. */
export function buildRplListMatch(
  programmeId: string,
  rplEventDetailIds: readonly string[],
) {
  const metadataMatches = rplEventDetailIds.flatMap(metadataContains);
  const matches: Record<string, unknown>[] = [
    {
      member: {
        registrations: {
          event: {
            detail: {
              programme_id: { _eq: programmeId },
              title: { _ilike: '%(RPL)%' },
            },
          },
        },
      },
    },
  ];

  if (metadataMatches.length) {
    matches.push({
      member: {
        enrollments: {
          archived: { _eq: false },
          status: { _nin: CLOSED_ENROLLMENT_STATUSES },
          _or: metadataMatches,
        },
      },
    });
  }

  return { _or: matches };
}

export function buildRplCountWhere(
  programmeId: string,
  rplEventDetailIds: readonly string[],
  statuses: readonly string[] = [],
) {
  return {
    _and: [
      { type: { _eq: 'STUDENT' } },
      { cohort: { programme_id: { _eq: programmeId } } },
      ...(statuses.length ? [{ status: { _in: [...statuses] } }] : []),
      buildRplListMatch(programmeId, rplEventDetailIds),
    ],
  };
}

export function isRplProgrammeLearner(
  original: {
    member?: {
      enrollments?: EnrollmentWithMetadata[];
      registrations?: Array<{ event?: Parameters<typeof isRplEvent>[0] }>;
    };
  },
  rplEventDetailIds: ReadonlySet<string>,
): boolean {
  if (
    enrollmentIncludesProgrammeRpl(
      original.member?.enrollments,
      rplEventDetailIds,
    )
  ) {
    return true;
  }

  return (
    original.member?.registrations?.some((registration) =>
      isRplEvent(registration.event),
    ) ?? false
  );
}
